import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { useApiQuery } from "@frontend/hooks/use-api"
import { useEffect, useMemo, useState } from "react"

interface CaseSeries {
  id: string
  series_description: string | null
  series_identifier: string
  modality: string | null
  image_type: string | null
  image_plane: string | null
  series_number: number | null
  number_of_frames: number | null
  voxel_size_z: number | null
  acquisition_datetime: string | null
}

interface ViewerVolume {
  url: string
  name: string
}

export function useViewerVolumes(caseId: string, selectedSeriesId?: string, options?: { loadFiles?: boolean }) {
  const { getAccessToken } = useAuthRuntime()
  const loadFiles = options?.loadFiles ?? true
  const [volumes, setVolumes] = useState<ViewerVolume[]>([])
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useApiQuery<{ data: CaseSeries[] }>(
    ["cases", caseId, "series"],
    `/cases/${caseId}/series`,
    { enabled: Boolean(caseId) }
  )

  const series = data?.data ?? []
  const activeSeries = useMemo(
    () => (selectedSeriesId ? series.filter((s) => s.id === selectedSeriesId) : series),
    [series, selectedSeriesId]
  )

  const _seriesKey = useMemo(() => activeSeries.map((s) => `${s.id}:${s.series_identifier}`).join("|"), [activeSeries])

  useEffect(() => {
    let cancelled = false
    const objectUrls: string[] = []

    async function load() {
      if (!(loadFiles && activeSeries.length)) {
        setVolumes([])
        setError(null)
        return
      }

      try {
        setError(null)
        const loaded = await Promise.all(
          activeSeries.map(async (s) => {
            const token = await getAccessToken()
            const headers = token ? { Authorization: `Bearer ${token}` } : undefined
            const res = await fetch(`/api/cases/${caseId}/series/${s.id}/file`, {
              headers
            })

            if (!res.ok) {
              throw new Error(`Failed to load series ${s.series_identifier}`)
            }

            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            objectUrls.push(url)
            const baseName = s.series_description ?? s.series_identifier
            return {
              url,
              name: baseName.endsWith(".nii.gz") || baseName.endsWith(".nii") ? baseName : `${baseName}.nii.gz`
            }
          })
        )

        if (!cancelled) {
          setVolumes(loaded)
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load viewer volumes")
          setVolumes([])
        }
      }
    }

    void load()

    return () => {
      cancelled = true
      for (const url of objectUrls) {
        URL.revokeObjectURL(url)
      }
    }
  }, [caseId, getAccessToken, loadFiles, activeSeries.length, activeSeries.map])

  return { volumes, isLoading, error, series }
}
